import { __assign } from 'tslib';
import React__default, { useState, useEffect } from 'react';
import { getUserThumbnailTemplatesSelector } from '@keepeek/commons';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Divider, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import DefaultIcon from '../../DefaultIcon/index.js';
import { currentUserSelector } from '../../providers/users/selectors.js';
import { withSuspense } from '../../withSuspense/index.js';
import { MAX_THUMBNAIL_TEMPLATE_TO_LOAD } from '../constants.js';

var CropSideBar = function CropSideBar(_a) {
    var onChangeOutDatas = _a.onChangeOutDatas, _b = _a.originalWidth, originalWidth = _b === void 0 ? 0 : _b, _c = _a.originalHeight, originalHeight = _c === void 0 ? 0 : _c, _d = _a.freeCropHeight, freeCropHeight = _d === void 0 ? originalHeight : _d, _f = _a.freeCropWidth, freeCropWidth = _f === void 0 ? originalWidth : _f, _g = _a.customCropHeight, customCropHeight = _g === void 0 ? originalHeight : _g, _h = _a.customCropWidth, customCropWidth = _h === void 0 ? originalWidth : _h, setCustomCrop = _a.setCustomCrop, customCrop = _a.customCrop, freeCrop = _a.freeCrop, setFreeCrop = _a.setFreeCrop, className = _a.className, id = _a.id, _j = _a.KIcon, KIcon = _j === void 0 ? DefaultIcon : _j;
    var t = useTranslation("refront-components").t;
    var currentUser = useRecoilValue(currentUserSelector);
    var thumbnailTemplatesApiResult = useRecoilValue(getUserThumbnailTemplatesSelector({
        userId: currentUser === null || currentUser === void 0 ? void 0 : currentUser.id,
        size: MAX_THUMBNAIL_TEMPLATE_TO_LOAD
    }));
    var thumbnailTemplates = thumbnailTemplatesApiResult &&
        thumbnailTemplatesApiResult._embedded &&
        thumbnailTemplatesApiResult._embedded["thumbnail-template"];
    var _k = useState(), selectedTemplate = _k[0], setSelectedTemplate = _k[1];
    var _l = useState(""), width = _l[0], setWidth = _l[1];
    var _m = useState(""), height = _m[0], setHeight = _m[1];
    var _o = useState("width"), fixValue = _o[0], setFixValue = _o[1];
    var handleOnClickCustomToggleButton = function () {
        setFreeCrop && setFreeCrop(false);
        setCustomCrop && setCustomCrop(true);
        setSelectedTemplate(undefined);
        if (width === "") {
            setWidth(customCropWidth);
        }
        if (height === "") {
            setHeight(customCropHeight);
        }
    };
    useEffect(function () {
        if (customCrop) {
            if (width !== "" && fixValue === "width") {
                var adaptedHeight = Math.round((width * customCropHeight) / customCropWidth);
                setHeight(adaptedHeight);
                onChangeOutDatas && onChangeOutDatas(adaptedHeight, width);
            }
            if (height !== "" && fixValue === "height") {
                var adaptedWidth = Math.round((height * customCropWidth) / customCropHeight);
                setWidth(adaptedWidth);
                onChangeOutDatas && onChangeOutDatas(height, adaptedWidth);
            }
        }
    }, [customCrop, customCropHeight, customCropWidth, fixValue, height, onChangeOutDatas, width]);
    var handleFreeCropClick = function () {
        onChangeOutDatas && onChangeOutDatas(undefined, undefined);
        setSelectedTemplate(undefined);
    };
    var handleFreeCropChange = function () {
        setCustomCrop && setCustomCrop(false);
        setHeight("");
        setWidth("");
        setFreeCrop && setFreeCrop(true);
    };
    var handleThumbnailTemplateChange = function (_, value) {
        setFreeCrop && setFreeCrop(false);
        setCustomCrop && setCustomCrop(false);
        value && setSelectedTemplate(value);
        var filter = value && (thumbnailTemplates === null || thumbnailTemplates === void 0 ? void 0 : thumbnailTemplates.filter(function (t) { return t.id === Number.parseInt(value); }));
        if (filter && filter.length === 1) {
            var thumbnailTemplate = filter[0];
            onChangeOutDatas && onChangeOutDatas(thumbnailTemplate.height, thumbnailTemplate.width);
            setHeight("");
            setWidth("");
        }
    };
    return (React__default.createElement(Box, { "data-testid": "CropSideBarTestId", id: id, className: className, sx: { display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" } },
        React__default.createElement(Box, { sx: {
                pt: 1.5,
                pr: 2,
                pb: 2,
                pl: 2
            } },
            React__default.createElement(Typography, { sx: __assign({ pb: 1 }, reserveScrollBarSpacesSx), variant: "h6" }, t("components.kf-thumbnail-generator.crop.frame")),
            React__default.createElement(ToggleButtonGroup, { exclusive: true, sx: __assign({ width: "100%" }, reserveScrollBarSpacesSx), value: freeCrop, onChange: handleFreeCropChange },
                React__default.createElement(ToggleButton, { sx: toggleButtonSx, color: "primary", value: true, onClick: handleFreeCropClick },
                    React__default.createElement(KIcon, { sx: {
                            fontSize: "2em"
                        }, className: "fa-regular fa-camera" }),
                    React__default.createElement(Box, { sx: {
                            fontWeight: "bold"
                        } }, t("components.kf-thumbnail-generator.crop.button.free")),
                    React__default.createElement(Box, { sx: {
                            textTransform: "lowercase",
                            flexGrow: "1",
                            textAlign: "right"
                        } }, "".concat(freeCropWidth, "x").concat(freeCropHeight, "px"))))),
        React__default.createElement(Divider, null),
        React__default.createElement(Box, { sx: {
                pt: 1.5,
                pr: 2,
                pb: 1,
                pl: 2
            } },
            React__default.createElement(Typography, { variant: "h6", sx: __assign({}, reserveScrollBarSpacesSx) }, t("components.kf-thumbnail-generator.crop.predefined.dimension"))),
        React__default.createElement(ToggleButtonGroup, { sx: __assign(__assign({ paddingLeft: 2, paddingRight: 2, paddingBottom: 2, width: "100%", flexGrow: 1 }, reserveScrollBarSpacesSx), { 
                /* Add shadow when detect scroll area : */
                background: "\n          /* Shadow Cover TOP */\n            linear-gradient(\n              white 30%,\n          rgba(255, 255, 255, 0)\n          ) center top,\n\n          /* Shadow Cover BOTTOM */\n          linear-gradient(\n          rgba(255, 255, 255, 0),\n          white 70%\n          ) center bottom,\n\n          /* Shadow TOP */\n          radial-gradient(\n          farthest-side at 50% 0,\n          rgba(0, 0, 0, 0.2),\n          rgba(0, 0, 0, 0)\n          ) center top,\n\n          /* Shadow BOTTOM */\n          radial-gradient(\n          farthest-side at 50% 100%,\n          rgba(0, 0, 0, 0.2),\n          rgba(0, 0, 0, 0)\n          ) center bottom", backgroundRepeat: "no-repeat", backgroundSize: "100% 40px, 100% 40px, 100% 14px, 100% 14px", backgroundAttachment: "local, local, scroll, scroll" }), orientation: "vertical", value: selectedTemplate, exclusive: true, onChange: handleThumbnailTemplateChange }, thumbnailTemplates === null || thumbnailTemplates === void 0 ? void 0 : thumbnailTemplates.map(function (t) { return (React__default.createElement(ToggleButton, { key: t.id, value: "".concat(t.id), color: "primary", sx: toggleButtonSx },
            React__default.createElement(KIcon, { sx: {
                    fontSize: "2em"
                }, className: "fa-regular ".concat(t.icon || "fa-file-image") }),
            React__default.createElement(Box, { sx: {
                    fontWeight: "bold",
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                }, title: t.label }, t.label),
            React__default.createElement(Box, { sx: {
                    textTransform: "lowercase",
                    flexGrow: "1",
                    textAlign: "right"
                } }, "".concat(t.width, "x").concat(t.height, "px")))); })),
        React__default.createElement(Divider, null),
        React__default.createElement(Box, { sx: {
                pt: 1.5,
                pr: 2,
                pb: 2,
                pl: 2
            } },
            React__default.createElement(Box, { sx: __assign({ pb: 1 }, reserveScrollBarSpacesSx) },
                React__default.createElement(Typography, { variant: "h6" }, t("components.kf-thumbnail-generator.custom.crop"))),
            React__default.createElement(Box, { sx: __assign({}, reserveScrollBarSpacesSx) },
                React__default.createElement(ToggleButtonGroup, { exclusive: true, sx: {
                        width: "100%"
                    }, onClick: handleOnClickCustomToggleButton, value: customCrop },
                    React__default.createElement(ToggleButton, { sx: { width: "100%" }, value: true, color: "primary", disableRipple: true },
                        React__default.createElement(Box, { sx: { display: "flex", flexWrap: "wrap", width: "100%" } },
                            React__default.createElement(Box, { sx: { display: "flex", paddingBottom: "4px" } },
                                React__default.createElement(Typography, { variant: "subtitle1", sx: { textTransform: "none" } }, t("components.kf-thumbnail-generator.custom.crop.title"))),
                            React__default.createElement(Box, { display: "flex", alignItems: "flex-end", sx: { width: "100%" } },
                                React__default.createElement(TextField, { "data-testid": "widthTestId-input", size: "small", sx: getInputFreeSizeSx("width", fixValue), label: t("components.kf-thumbnail-generator.crop.width"), type: "number", value: width, onClick: function (_e) {
                                        setCustomCrop && setCustomCrop(true);
                                    }, onChange: function (_a) {
                                        var target = _a.target;
                                        setWidth(Number.parseInt(target.value));
                                        setFixValue("width");
                                    }, fullWidth: true }),
                                React__default.createElement(Typography, { sx: { padding: "8px" } }, "x"),
                                React__default.createElement(TextField, { "data-testid": "heightTestId-input", size: "small", sx: getInputFreeSizeSx("height", fixValue), fullWidth: true, label: t("components.kf-thumbnail-generator.crop.height"), type: "number", value: height, onClick: function (_e) {
                                        setCustomCrop && setCustomCrop(true);
                                    }, onChange: function (_a) {
                                        var target = _a.target;
                                        setHeight(Number.parseInt(target.value));
                                        setFixValue("height");
                                    } })))))))));
};
/**
 * we reserve the scrollbar space for all containers
 * because the thumbnail template container can have
 * a scrollbar and we want to keep a similar alignment
 * between the different components (with or without scrollbar)
 */
var reserveScrollBarSpacesSx = {
    scrollbarGutter: "stable both-edges",
    overflowY: "auto"
};
var toggleButtonSx = {
    display: "flex",
    width: "100%",
    alignItems: "center",
    textTransform: "none",
    gap: 1
};
var getInputFreeSizeSx = function (type, fixValue) {
    return {
        flex: 1,
        textTransform: "none",
        textAlign: "left",
        "& .MuiInputBase-input": {
            backgroundColor: "white",
            textAlign: "center",
            color: (type === "width" && fixValue === "height") || (type === "height" && fixValue === "width")
                ? "#c0c0c0"
                : "black"
        }
    };
};
var CropSideBar$1 = withSuspense(CropSideBar);

export { CropSideBar$1 as default };
//# sourceMappingURL=index.js.map
